import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faPaperPlane, faFileAlt, faPaperclip } from "@fortawesome/free-solid-svg-icons";
import {
  TaskPopupFrame,
  PopupSectionCard,
  PopupActionButton,
  PopupEmployeeInfoCard,
  resolveTaskTypeLabel,
} from "./PopupUiKit";
import {
  getTasksByEmployeeAndDate,
  getReportUnlock,
  openReportUnlock,
} from "../../../../../api/hrm/tasks/task_info";
import { workDayToApiParam } from "../../../../../utils/workDayApiParam";
import dayjs from "../../../../../utils/dayjs";
import { isTasksAdminEmail } from "../../../../../utils/auth";
import {
  showSuccessToast,
  showErrorToast,
  showWarningToast,
  showConfirmDialog,
} from "../../../../../components/ConfirmDialog";

const ALLOWED_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const MAX_FILE_SIZE_MB = 25;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
const VN_TZ = "Asia/Ho_Chi_Minh";


const isBefore21hVN = () => {
  const nowVN = new Date().toLocaleTimeString("vi-VN", {
    timeZone: VN_TZ,
    hour12: false,
  });

  const [hour, minute] = nowVN.split(":").map(Number);
  const totalMinutes = hour * 60 + minute;

  return totalMinutes < 21 * 60; // trước 21h
};

/** Ngày làm còn hiệu lực để báo cáo: hôm nay hoặc tương lai (theo giờ VN). */
const isReportableWorkDay = (workDay) => {
  if (workDay == null || workDay === "") return false;
  const workDate = dayjs(workDay).tz(VN_TZ).startOf("day");
  if (!workDate.isValid()) return false;
  const today = dayjs().tz(VN_TZ).startOf("day");
  return workDate.diff(today, "day") >= 0;
};

const TaskPopupApproved = ({
  isOpen,
  onClose,
  employeeId,
  employeeName,
  branchName,
  workDay,
  taskList = [],
  onReport,
  onUpdated,
}) => {
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = isTasksAdminEmail(currentUser);

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingIndex, setSendingIndex] = useState(null);
  const [isBefore21h, setIsBefore21h] = useState(true);
  const [unlockActive, setUnlockActive] = useState(false);
  const [unlockExpiresAt, setUnlockExpiresAt] = useState(null);
  const [unlocking, setUnlocking] = useState(false);

  
  const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

  // Tự tính trong popup (không phụ thuộc cờ từ card / thông báo)
  const canReportByUser = String(currentUser?.id || "") === String(employeeId || "");
  const canReportByDay = isReportableWorkDay(workDay);
  const canShowReportArea =
    canReportByUser && (canReportByDay || unlockActive);
  // Unlock bổ sung: bỏ cửa sổ 21h; ngày thường vẫn giữ trước 21h
  const timeOkForSubmit = unlockActive || isBefore21h;
  const canSubmitReport = canShowReportArea && timeOkForSubmit;
  const canCapture = isMobile && canShowReportArea && timeOkForSubmit;
  const reportBlockReason = !canReportByUser
    ? "Chỉ tài khoản của nhân viên trên phiếu mới có thể gửi báo cáo."
    : "Đã quá ngày làm việc — không thể gửi báo cáo cho ngày này.";
  const showAdminUnlockButton =
    isAdmin && !canReportByDay && !loading && reports.length > 0;

  // 🧠 Fetch công việc đã duyệt trong ngày
  const fetchReports = async () => {
    try {
      if (!employeeId || !workDay) return;
      setLoading(true);
      const dayParam = workDayToApiParam(workDay);
      const data = await getTasksByEmployeeAndDate(employeeId, dayParam, "approved");
      const list =
        data?.items?.map((t) => ({
          id: t.id,
          taskname: t.taskname,
          note: t.note,
          task_type: t.task_type,
          attachment_url: t.attachment_url,
          attachment_mime_type: t.attachment_mime_type,
          assigner_name: t.assigner_name,
          assigned_at: t.assigned_at,
          work_time: t.work_time,
          report_content: "",
          attachments: [],   // nhiều file (pdf / image) – KHÔNG GPS
          image: null,       // 1 ảnh chụp hiện trường có GPS (mobile)
          sent: false,
        })) || [];
      setReports(list);
    } catch (err) {
      console.error("❌ Lỗi tải chi tiết công việc:", err);
      showErrorToast("Không thể tải danh sách công việc chi tiết.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUnlock = async () => {
    try {
      if (!employeeId || !workDay) {
        setUnlockActive(false);
        setUnlockExpiresAt(null);
        return;
      }
      const dayParam = workDayToApiParam(workDay);
      const data = await getReportUnlock(employeeId, dayParam);
      setUnlockActive(!!data?.active);
      setUnlockExpiresAt(data?.expires_at || null);
    } catch (err) {
      console.error("❌ Lỗi kiểm tra unlock báo cáo:", err);
      setUnlockActive(false);
      setUnlockExpiresAt(null);
    }
  };

  const handleOpenUnlock = async () => {
    if (!employeeId || !workDay) return;
    const dayParam = workDayToApiParam(workDay);
    const dayLabel = dayjs(dayParam).isValid()
      ? dayjs(dayParam).tz(VN_TZ).format("DD/MM/YYYY")
      : dayParam;
    const confirm = await showConfirmDialog({
      title: "Mở báo cáo bổ sung?",
      text: `Cho phép ${employeeName || "nhân viên"} gửi báo cáo ngày ${dayLabel} trong 24 giờ tới.`,
      confirmText: "Mở 24h",
      cancelText: "Hủy",
    });
    if (!confirm?.isConfirmed) return;

    try {
      setUnlocking(true);
      const data = await openReportUnlock({
        employee_id: employeeId,
        work_day: dayParam,
      });
      setUnlockActive(true);
      setUnlockExpiresAt(data?.expires_at || null);
      showSuccessToast(data?.message || "Đã mở báo cáo bổ sung trong 24 giờ.");
    } catch (err) {
      showErrorToast(err?.message || "Không thể mở báo cáo bổ sung.");
    } finally {
      setUnlocking(false);
    }
  };

  useEffect(() => {
    if (isOpen && employeeId && workDay) {
      fetchReports();
      fetchUnlock();
    }
  }, [isOpen, employeeId, workDay]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    const checkTime = () => {
      setIsBefore21h(isBefore21hVN());
    };
  
    checkTime();
    const timer = setInterval(checkTime, 60000); // mỗi 1 phút
    return () => clearInterval(timer);
  }, []);

  // 📍 Lấy vị trí GPS
  const getLocation = async () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject("Thiết bị không hỗ trợ định vị");
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => reject(err.message),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  // 🧹 Validate 1 file (pdf / image)
  const validateSingleFile = (file) => {
    if (!file) return false;

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      showWarningToast("Chỉ cho phép file PDF hoặc ảnh JPG/PNG.");
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      showWarningToast(`Kích thước mỗi file không vượt quá ${MAX_FILE_SIZE_MB} MB.`);
      return false;
    }

    return true;
  };

  // 📎 Đính kèm nhiều file (chọn từ thư mục / drag & drop) – KHÔNG GPS
  const handleAttachFiles = (index, fileList) => {
    if (!fileList || fileList.length === 0) return;

    const newReports = [...reports];
    const target = newReports[index];

    if (!target.attachments) target.attachments = [];

    const currentCount = target.attachments.length;
    const incomingFiles = Array.from(fileList);
    const totalAfterAdd = currentCount + incomingFiles.length;

    // ❗ Nếu tổng số file vượt quá 10 → chặn ngay từ đầu
    if (totalAfterAdd > 10) {
      const allowed = 10 - currentCount;
      if (allowed <= 0) {
        showWarningToast("Bạn đã đạt tối đa 10 file.");
        return;
      } else {
        showWarningToast(`Chỉ có thể thêm tối đa ${allowed} file nữa.`);
      }
    }

    let addedCount = 0;

    for (const file of incomingFiles) {
      if (target.attachments.length >= 10) break; // bảo vệ cứng

      if (!validateSingleFile(file)) continue;

      const isImage = ["image/jpeg", "image/png"].includes(file.type);

      target.attachments.push({
        file,
        // previewUrl chỉ dùng để hiển thị icon/ảnh (nếu là image)
        previewUrl: isImage ? URL.createObjectURL(file) : null,
      });

      addedCount++;
    }

    if (addedCount > 0) {
      setReports(newReports);
    }
  };

  // ❌ Xóa 1 file đính kèm
  const handleRemoveAttachment = (reportIndex, attachIndex) => {
    const newReports = [...reports];
    const r = newReports[reportIndex];
    if (!r.attachments) return;

    const removed = r.attachments[attachIndex];
    if (removed?.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl);
    }

    r.attachments.splice(attachIndex, 1);
    setReports(newReports);
  };

  // 📸 Xử lý chụp ảnh (mobile camera) – CHỈ 1 ẢNH + GPS
  const handleCapture = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Chỉ cho phép ảnh chụp (jpg/png)
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      showWarningToast("Ảnh chụp chỉ chấp nhận JPG hoặc PNG.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showWarningToast(`Kích thước ảnh không vượt quá ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    try {
      const location = await getLocation();
      const url = URL.createObjectURL(file);

      const newReports = [...reports];
      const target = newReports[index];

      // Nếu đã có ảnh trước đó thì thu hồi URL cũ
      if (target.image?.previewUrl) {
        URL.revokeObjectURL(target.image.previewUrl);
      }

      target.image = {
        file,
        previewUrl: url,
        location,
      };

      setReports(newReports);
    } catch (error) {
      console.error("❌ Lỗi lấy vị trí:", error);
      showErrorToast("Không lấy được vị trí: " + error);
    }
  };

  // Nếu chuỗi từ BE dạng "2025-11-17 13:58:00.123456"
  const formatAssignedAt = (value) => {
    if (!value) return "";

    // 1️⃣ Nếu là Date object -> format luôn
    if (value instanceof Date) {
      return dayjs(value).format("HH:mm DD-MM-YYYY");
    }

    let str = String(value);

    // 2️⃣ Chuẩn hoá: "2025-11-17 13:58:00" -> "2025-11-17T13:58:00"
    if (str.includes(" ") && !str.includes("T")) {
      const [datePart, timePart] = str.split(" ");
      const cleanTime = timePart.split(".")[0];
      str = `${datePart}T${cleanTime}`;
    }

    const d = dayjs(str);
    if (!d.isValid()) return "";

    // 3️⃣ Hiển thị giờ VN
    return d.format("HH:mm DD-MM-YYYY");
  };

  const formatWorkTime = (t) => {
    if (!t) return "";
    return t.substring(0, 5); // lấy HH:mm
  };

  // ✏️ Nhập nội dung báo cáo
  const handleChangeText = (index, value) => {
    const newReports = [...reports];
    newReports[index].report_content = value;
    setReports(newReports);
  };

  // 🚀 Gửi báo cáo từng task
  const handleSubmitOne = async (index) => {
    const r = reports[index];
    if (!r.report_content.trim()) {
      showWarningToast("Vui lòng nhập nội dung báo cáo.");
      return;
    }

    setSendingIndex(index);

    try {
      const payload = new FormData();
      payload.append("report_content", r.report_content);
      payload.append("reported_by", currentUser.id);

      // Ảnh chụp hiện trường (1 ảnh + location)
      if (r.image?.file) {
        payload.append("image", r.image.file);
        if (r.image.location) {
          payload.append(
            "report_image_location",
            JSON.stringify(r.image.location)
          );
        }
      }

      // Gửi danh sách file đính kèm (PDF/Ảnh)
      const attachments = r.attachments || [];
      attachments.forEach((att) => {
        if (att.file) {
          payload.append("files", att.file);
        }
      });

      await onReport({ id: r.id }, payload);
      showSuccessToast("Đã gửi báo cáo thành công!");

      const newReports = [...reports];
      newReports[index].sent = true;
      setReports(newReports);
      onUpdated?.();
    } catch (err) {
      console.error("❌ Lỗi gửi báo cáo:", err);
      showErrorToast(
        err?.message || "Không thể gửi báo cáo. Vui lòng thử lại."
      );
    } finally {
      setSendingIndex(null);
    }
  };

  const formattedDate = dayjs(workDay).isValid()
    ? dayjs(workDay).tz(VN_TZ).format("DD/MM/YYYY")
    : "—";
  const typeFromLoaded = resolveTaskTypeLabel(reports);
  const typeFromCard = resolveTaskTypeLabel(taskList);
  const taskTypeLabel =
    typeFromLoaded !== "—" ? typeFromLoaded : typeFromCard;
  const branchDisplay =
    branchName || reports?.[0]?.branch_name || "—";

  return (
    <TaskPopupFrame
      isOpen={isOpen}
      onClose={onClose}
      maxWidthClass="max-w-4xl"
      titleIcon={<FontAwesomeIcon icon={faFileAlt} className="text-red-600" />}
      title="Báo cáo"
      titleHighlight="công việc"
    >
      <PopupEmployeeInfoCard
        branchName={branchDisplay}
        employeeName={employeeName}
        workDay={formattedDate}
        taskTypeLabel={taskTypeLabel}
      />

      {showAdminUnlockButton && (
        <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="text-sm text-amber-900">
            {unlockActive && unlockExpiresAt ? (
              <p>
                Đã mở báo cáo bổ sung đến{" "}
                <span className="font-semibold">
                  {dayjs(unlockExpiresAt).tz(VN_TZ).format("HH:mm DD/MM/YYYY")}
                </span>
                . Có thể mở lại để gia hạn thêm 24h.
              </p>
            ) : (
              <p>
                Ngày làm đã quá hạn. Mở khóa để nhân viên tự gửi báo cáo trong
                24 giờ.
              </p>
            )}
          </div>
          <PopupActionButton
            onClick={handleOpenUnlock}
            disabled={unlocking}
            className="bg-amber-600 hover:bg-amber-700 shrink-0"
          >
            {unlocking
              ? "Đang mở..."
              : unlockActive
              ? "Gia hạn thêm 24h"
              : "Mở báo cáo bổ sung (24h)"}
          </PopupActionButton>
        </div>
      )}

                {loading ? (
                  <p className="text-gray-500 text-sm italic bg-white rounded-2xl border border-gray-300 p-4">Đang tải công việc...</p>
                ) : reports.length === 0 ? (
                  <p className="text-gray-500 text-sm italic bg-white rounded-2xl border border-gray-300 p-4">Không có công việc đã duyệt.</p>
                ) : (
                  reports.map((r, i) => {
                    return (
                      <PopupSectionCard
                        key={i}
                        className={`${
                          r.sent ? "opacity-90 border-emerald-200" : ""
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="font-semibold text-gray-800">
                            {i + 1}. {r.taskname}
                          </div>

                          {/* 🏷️ Tag “Giao việc” màu đỏ nằm góc phải */}
                          {r.task_type === "assigned" && (
                            <span className="text-[10px] bg-red-50 text-red-700 px-2.5 py-1 rounded-full border border-red-200 font-bold uppercase tracking-wider shadow-sm">
                              Yêu cầu trước {formatWorkTime(r.work_time)}
                            </span>
                          )}
                        </div>

                        {r.note && (
                          <div className="text-xs text-gray-500 mb-1 italic">
                            Ghi chú: {r.note}
                          </div>
                        )}

                        {/* File đính kèm gốc từ người giao việc */}
                        {r.attachment_url && (
                          <button
                            onClick={() => window.open(r.attachment_url, "_blank")}
                            className="
                              flex items-center gap-2
                              bg-gray-100 hover:bg-gray-200
                              text-gray-700 text-[11px] font-bold uppercase tracking-wide
                              px-3 py-1.5 rounded-xl border border-gray-200 transition
                            "
                            title="Mở file đính kèm"
                          >
                            <FontAwesomeIcon
                              icon={faPaperclip}
                              className="text-gray-500 text-sm"
                            />
                            <span>File giao việc</span>
                          </button>
                        )}

                        {/* Người giao việc */}
                        {r.assigner_name && (
                          <div className="text-xs text-red-500 italic mb-1">
                            Được giao bởi: {r.assigner_name}
                            {r.assigned_at && (
                              <span className="ml-1">
                                ({formatAssignedAt(r.assigned_at)})
                              </span>
                            )}
                          </div>
                        )}

                        {/* Nội dung báo cáo */}
                        {canShowReportArea ? (
                          <>
                            {unlockActive && unlockExpiresAt && (
                              <p className="text-xs text-amber-700 italic mb-2">
                                Được mở báo cáo bổ sung đến{" "}
                                {dayjs(unlockExpiresAt)
                                  .tz(VN_TZ)
                                  .format("HH:mm DD/MM/YYYY")}
                                .
                              </p>
                            )}
                            {!timeOkForSubmit && (
                              <p className="text-xs text-red-500 italic mb-2">
                                Chỉ cho phép báo cáo công việc trước 21:00.
                              </p>
                            )}

                            {/* Nội dung báo cáo */}
                            <textarea
                              value={r.report_content}
                              onChange={(e) => handleChangeText(i, e.target.value)}
                              rows={3}
                              placeholder="Nhập nội dung báo cáo..."
                              disabled={r.sent || !timeOkForSubmit}
                              maxLength={250}
                              className="mb-2 w-full resize-none rounded-xl border border-gray-300 bg-white p-3 text-sm outline-none transition focus:border-red-500 focus:ring-4 focus:ring-red-500/5"
                            />
                            <p
                              className={`text-xs text-right ${
                                r.report_content.length > 200
                                  ? "text-red-500 font-medium"
                                  : "text-gray-500"
                              }`}
                            >
                              {r.report_content.length}/250 ký tự
                            </p>

                            {/* File đính kèm */}
                            <div className="mb-3">
                              <label className="text-sm font-medium flex items-center gap-1 mb-1">
                                📎 File đính kèm (PDF / Ảnh)
                              </label>

                              <div
                                className={`cursor-pointer rounded-xl border-2 border-dashed p-4 text-center text-sm ${
                                  r.sent
                                    ? "border-gray-300 bg-gray-100 text-gray-400"
                                    : "border-gray-300 hover:border-red-400 hover:bg-red-50/40"
                                }`}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  if (r.sent) return;
                                  handleAttachFiles(i, e.dataTransfer.files);
                                }}
                              >
                                <input
                                  type="file"
                                  accept="application/pdf,image/jpeg,image/png"
                                  id={`file_${i}`}
                                  className="hidden"
                                  multiple
                                  onChange={(e) =>
                                    handleAttachFiles(i, e.target.files)
                                  }
                                  disabled={r.sent}
                                />

                                <div className="relative mb-1">
                                  {/* Label căn giữa */}
                                  <label
                                    htmlFor={`file_${i}`}
                                    className={`block text-center cursor-pointer ${
                                      r.sent
                                        ? "text-gray-400 cursor-not-allowed"
                                        : "text-blue-600 underline"
                                    }`}
                                  >
                                    Chọn tệp để tải lên
                                  </label>

                                  {/* Counter – góc phải */}
                                  <span className="absolute right-0 top-0 text-xs text-gray-500">
                                    {(r.attachments?.length || 0)} / 10 files
                                  </span>
                                </div>

                                <p className="text-xs text-gray-500 text-center mt-1">
                                  (Chỉ nhận PDF, JPG, PNG. Tối đa {MAX_FILE_SIZE_MB} MB / file)
                                </p>

                                {/* Danh sách file đã chọn */}
                                {r.attachments && r.attachments.length > 0 && (
                                  <div className="mt-3 space-y-1 text-left">
                                    {r.attachments.map((att, idx2) => {
                                      const isPdf =
                                        att.file?.type === "application/pdf";
                                      const sizeMb =
                                        att.file && att.file.size
                                          ? (att.file.size / 1024 / 1024).toFixed(1)
                                          : null;

                                      return (
                                        <div
                                          key={idx2}
                                          className="flex items-center justify-between text-sm bg-white rounded-md px-2 py-1 border"
                                        >
                                          <div className="flex items-center gap-2 overflow-hidden">
                                            <span>
                                              {isPdf ? "📄" : "🖼️"}
                                            </span>
                                            <span className="truncate max-w-[180px]">
                                              {att.file?.name || "Tệp đính kèm"}
                                            </span>
                                            {sizeMb && (
                                              <span className="text-xs text-gray-500">
                                                ({sizeMb} MB)
                                              </span>
                                            )}
                                          </div>
                                          {!r.sent && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleRemoveAttachment(i, idx2)
                                              }
                                              className="text-red-500 text-xs underline hover:text-red-600"
                                            >
                                              Xóa
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Ảnh chụp hiện trường (chỉ hiển thị trên MOBILE) */}
                            {isMobile && (
                              <div className="mb-3">

                                {!canCapture ? (
                                  <p className="text-xs text-gray-500 italic">
                                    Chỉ chụp ảnh bằng điện thoại (không hỗ trợ trên máy tính).
                                  </p>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-3 mb-2">
                                      <label
                                        htmlFor={`camera_${i}`}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer
                                          ${
                                            r.sent
                                              ? "bg-gray-100 text-gray-400 border border-gray-300 cursor-not-allowed"
                                              : "bg-white text-green-700 border border-green-500 hover:bg-green-50 hover:shadow-sm"
                                          }`}
                                      >
                                        <FontAwesomeIcon icon={faCamera} />
                                        <span>{r.image ? "Chụp lại ảnh" : "Chụp ảnh"}</span>
                                      </label>

                                      <input
                                        id={`camera_${i}`}
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        className="hidden"
                                        onChange={(e) => handleCapture(i, e)}
                                        disabled={r.sent}
                                      />
                                    </div>

                                    {r.image && (
                                      <div className="flex items-center gap-3 flex-wrap">
                                        <div className="flex items-center gap-2">
                                          <img
                                            src={r.image.previewUrl}
                                            alt="preview"
                                            className="w-16 h-16 object-cover rounded-md border"
                                          />
                                          {r.image.location && (
                                            <div className="text-xs text-gray-600">
                                              📍 {r.image.location.lat?.toFixed(4)}, {r.image.location.lng?.toFixed(4)}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}

                            {/* Nút gửi */}
                            {canSubmitReport && (
                              <div className="flex justify-end">
                                <PopupActionButton
                                  onClick={() => handleSubmitOne(i)}
                                  disabled={r.sent || sendingIndex === i || !timeOkForSubmit}
                                  className={`${
                                    !timeOkForSubmit || sendingIndex === i
                                      ? "bg-gray-400 hover:bg-gray-400"
                                      : r.sent
                                      ? "bg-emerald-400 hover:bg-emerald-400"
                                      : ""
                                  }`}
                                >
                                  <FontAwesomeIcon icon={faPaperPlane} />
                                  {r.sent
                                    ? "Đã gửi"
                                    : sendingIndex === i
                                    ? "Đang gửi..."
                                    : "Gửi báo cáo"}
                                </PopupActionButton>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-gray-400 italic">
                            {reportBlockReason}
                          </p>
                        )}
                      </PopupSectionCard>
                    );
                  })
                )}
    </TaskPopupFrame>
  );
};

export default TaskPopupApproved;
